import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Alert, 
  InputAdornment, 
  IconButton, 
  Link, 
  CircularProgress, 
  FormControlLabel, 
  Checkbox,
  
  Fade,
  Card,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon,
  AdminPanelSettings,
  Security,
  Hotel
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import backgroundImage from '../../../assets/images/hot.webp';

// =============================
// 🎨 MUI THEME CONFIGURATION (Updated to match HotelWebsite)
// =============================
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f8f9fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#1976d2',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          borderRadius: 8,
          boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.4)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: '#1976d2',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

// =============================
// 🚀 LOGIN COMPONENT
// =============================
const LoginAdminStaff = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // ✅ Load saved email if "Remember Me" is checked
  useEffect(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, []);

  // ✅ Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Both email and password are required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/staff/login2.php`,
        { email: email.trim(), password },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        }
      );

      const data = response.data;
      console.log('Login Response:', data);

      if (data.access_token) {
        localStorage.setItem('authToken', data.access_token);
        localStorage.setItem('userProfile', JSON.stringify(data.user));

        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('email', email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('email');
        }

        // Redirect by role
        if (data.user?.user_type === 'receptionist') {
          navigate('/hotel/reception/dashboard');
        } 
        
        else if (data.user?.user_type === 'Restaurant/Bar') {
      navigate('/hotel/fb/dashboard');
        }

          else if (data.user?.user_type === 'Account') {
      navigate('/hotel/Acc/Dashboard');
        }
           else if (data.user?.user_type === 'Cashier') {
      navigate('/hotel/Cashier/Dashboard');
        }


         else if (data.user?.user_type === 'Storekeeping') {
      navigate('/hotel/Storekeeping/Dashboard');
        }

 else if (data.user?.user_type === 'Requisition') {
      navigate('/hotel/requisition/Dashboard_requisition');
        }

        else if (data.user?.user_type === 'hod') {
      navigate('/hotel/hod/Dashboard_hod');
        }

 else if (data.user?.user_type === 'General_Manager') {
      navigate('/hotel/gm/Dashboard_gm');
        }


        else if (data.user?.user_type === 'hr') {
      navigate('/hotel/HR/Dashboard_hr');
        }



        else {
          navigate('/HotelStaff');
        }

        setError('');
      } else {
        setError('Invalid credentials or server response.');
      }
    } catch (err) {
      console.error('Login error:', err);

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout. Please try again.');
      } else if (err.request) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          padding: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background overlay matching HotelWebsite theme */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(33, 150, 243, 0.8) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={800}>
            <Card
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 20px 60px 0 rgba(25, 118, 210, 0.25)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1565c0 100%)',
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white',
                    mb: 3,
                    boxShadow: '0 8px 25px 0 rgba(25, 118, 210, 0.4)',
                  }}
                >
                  <Hotel sx={{ fontSize: 40 }} />
                </Box>

                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    textAlign: 'center',
                    mb: 1,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold',
                  }}
                >
                 Hotel Management
                </Typography>

                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                  Staff Portal
                </Typography>

                <Typography variant="body2" color="primary" fontWeight={600}>
                  Administrative Access
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdminPanelSettings color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => setShowPassword(!showPassword)}
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        color="primary"
                        sx={{ 
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          '&.Mui-checked': {
                            color: 'primary.main',
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        Remember me
                      </Typography>
                    }
                  />

                  <Link href="#" variant="body2" underline="hover" color="primary" sx={{ fontWeight: 600 }}>
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={!loading && <LoginIcon />}
                  disabled={loading || !email || !password}
                  sx={{ 
                    py: 1.5, 
                    fontSize: '1.1rem', 
                    fontWeight: 600, 
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In to Dashboard'}
                </Button>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Secure access for authorized hotel staff only
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  © {new Date().getFullYear()} e-Neurons Luxury Hospitality System
                </Typography>
              </Box>
            </Card>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LoginAdminStaff;